import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, foreignDefaults, isForeign, pivaOk, cfOk, ALIQUOTE, NATURE, MODALITA } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Field } from "@/components/Common";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export const EMPTY_CUSTOMER = { tipo: "cliente", denominazione: "", nome: "", cognome: "", piva: "", codice_fiscale: "", indirizzo: "", cap: "",
  comune: "", provincia: "", nazione: "IT", codice_destinatario: "0000000", pec: "", email: "", telefono: "" };
export const EMPTY_PRODUCT = { codice: "", descrizione: "", prezzo: 0, aliquota: 22, natura: "", unita: "", a_magazzino: false, scorta_minima: 0, barcode: "", fornitore_id: "" };

const FIELDS = [
  ["denominazione", "Ragione sociale", "sm:col-span-2"], ["nome", "Nome (persona fisica)"], ["cognome", "Cognome"],
  ["piva", "Partita IVA"], ["codice_fiscale", "Codice fiscale"], ["indirizzo", "Indirizzo", "sm:col-span-2"],
  ["cap", "CAP"], ["comune", "Comune / Città"], ["provincia", "Provincia (sigla)"], ["nazione", "Nazione (IT, FR, DE, SM, US…)"],
  ["email", "Email"], ["telefono", "Telefono"],
];
export const TAB_LIST = "flex h-auto w-full flex-wrap justify-start gap-1 bg-sky-100 p-1";
export const TAB_TRIGGER = "flex-1 whitespace-nowrap py-2 text-xs text-sky-900 sm:text-sm data-[state=active]:bg-sky-600 data-[state=active]:text-white";
export const TAB_PANEL = "mt-3 rounded-lg border border-sky-200 bg-white p-4 sm:p-6";
const GRID = "grid gap-4 sm:grid-cols-2 lg:grid-cols-4";
const FE_FIELDS = [["codice_destinatario", "Codice destinatario SdI"], ["pec", "PEC"]];
const RAPP_FIELDS = [["sconto", "Sconti (es. 10+5)"], ["fido", "Fido €"], ["iban", "Coordinate bancarie cliente (IBAN)"], ["banca_ditta", "IBAN banca ditta (dove il cliente paga)"], ["trasporto", "Incaricato del trasporto"], ["porto", "Porto (franco, assegnato…)"]];

const useDraft = (value) => {
  const [edit, setEdit] = useState(value);
  useEffect(() => { setEdit(value); }, [value]);
  return [edit, setEdit];
};

const ForeignHint = ({ naz }) => (
  <p data-testid="customer-foreign-hint" className="rounded-md bg-sky-50 p-3 text-xs text-sky-900 sm:col-span-2">
    {naz.toUpperCase() === "SM"
      ? "San Marino: codice destinatario 2R4GTO8, Provincia EE. In Partita IVA metti il Codice Operatore Economico (solo numeri). Codice fiscale solo se consumatore finale."
      : "Soggetto estero: codice destinatario XXXXXXX, CAP 00000, Provincia EE. Partita IVA fino a 28 caratteri; se non c'è viene usato 00000000000."}
  </p>
);

export const CustomerDialog = ({ value, onClose, onSaved }) => {
  const [edit, setEdit] = useDraft(value);
  const [agenti, setAgenti] = useState([]);
  useEffect(() => {
    if (value) api.get("/users").then((r) => setAgenti(r.data.filter((u) => u.tenant_role === "agente"))).catch(() => setAgenti([]));
  }, [value]);
  const save = async () => {
    try {
      const body = { ...edit, fido: Number(edit.fido) || 0 };
      const { data } = edit.id ? await api.put(`/customers/${edit.id}`, body) : await api.post("/customers", body);
      toast.success("Anagrafica salvata");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="h-[95vh] max-h-[95vh] w-[98vw] max-w-[98vw] content-start overflow-y-auto border-sky-200 bg-sky-50 sm:rounded-xl" data-testid="customer-dialog">
        <DialogHeader><DialogTitle>{edit?.id ? "Modifica anagrafica" : "Nuova anagrafica"}</DialogTitle></DialogHeader>
        {edit && (
          <Tabs defaultValue="anagrafica" className="min-w-0">
          <TabsList className={TAB_LIST}>
            <TabsTrigger className={TAB_TRIGGER} value="anagrafica" data-testid="cd-tab-anagrafica">Dati anagrafici</TabsTrigger>
            <TabsTrigger className={TAB_TRIGGER} value="fe" data-testid="cd-tab-fe">Fattura elettronica</TabsTrigger>
            <TabsTrigger className={TAB_TRIGGER} value="rapporti" data-testid="cd-tab-rapporti">Rapporti commerciali</TabsTrigger>
          </TabsList>
          <TabsContent value="anagrafica" className={TAB_PANEL}>
          <div className={GRID}>
            <Field label="Tipo">
              <Select value={edit.tipo} onValueChange={(v) => setEdit({ ...edit, tipo: v })}>
                <SelectTrigger data-testid="customer-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cliente">Cliente</SelectItem><SelectItem value="fornitore">Fornitore</SelectItem><SelectItem value="entrambi">Cliente e fornitore</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Codice anagrafica"><Input data-testid="customer-codice-input" placeholder="Es. BRUSAL" value={edit.codice || ""} onChange={(e) => setEdit({ ...edit, codice: e.target.value })} /></Field>
            {FIELDS.map(([k, label, cls]) => (
              <Field key={k} label={label} className={cls}>
                <Input data-testid={`customer-${k}-input`} maxLength={k === "nazione" ? 2 : undefined} value={edit[k] || ""}
                  onChange={(e) => setEdit(k === "nazione" ? { ...edit, ...foreignDefaults(e.target.value, edit) } : { ...edit, [k]: e.target.value })} />
                {!isForeign(edit.nazione) && k === "piva" && edit.piva && !pivaOk(edit.piva) && <p data-testid="customer-piva-error" className="text-xs text-rose-600">Partita IVA non valida</p>}
                {!isForeign(edit.nazione) && k === "codice_fiscale" && edit.codice_fiscale && !cfOk(edit.codice_fiscale) && <p data-testid="customer-cf-error" className="text-xs text-rose-600">Codice fiscale non valido</p>}
              </Field>
            ))}
            {isForeign(edit.nazione) && <ForeignHint naz={edit.nazione} />}
          </div>
          </TabsContent>
          <TabsContent value="fe" className={TAB_PANEL}>
          <div className={GRID}>
            <label className="flex items-center justify-between rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm sm:col-span-2">
              <span><b>Pubblica amministrazione</b><br /><span className="text-xs text-slate-500">Codice univoco ufficio IPA di 6 caratteri; in fattura viene proposto lo split payment</span></span>
              <Switch data-testid="customer-pa-switch" checked={!!edit.pa} onCheckedChange={(v) => setEdit({ ...edit, pa: v, codice_destinatario: v ? (edit.codice_destinatario?.length === 6 ? edit.codice_destinatario : "") : edit.codice_destinatario || "0000000" })} />
            </label>
            {FE_FIELDS.map(([k, label]) => (
              <Field key={k} label={k === "codice_destinatario" && edit.pa ? "Codice univoco ufficio IPA (6 caratteri)" : label}><Input data-testid={`customer-${k}-input`} maxLength={k === "codice_destinatario" ? (edit.pa ? 6 : 7) : undefined} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: k === "codice_destinatario" ? e.target.value.toUpperCase() : e.target.value })} /></Field>
            ))}
            <p className="text-xs text-slate-500 sm:col-span-2">PA: codice univoco ufficio di 6 caratteri. Privati: codice di 7 caratteri o 0000000 con PEC. Esteri: XXXXXXX.</p>
          </div>
          </TabsContent>
          <TabsContent value="rapporti" className={TAB_PANEL}>
          <div className={GRID}>
            {RAPP_FIELDS.map(([k, label]) => (
              <Field key={k} label={label}><Input data-testid={`customer-${k}-input`} type={k === "fido" ? "number" : "text"} value={edit[k] ?? ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} /></Field>
            ))}
            <Field label="Modalità di pagamento">
              <Select value={edit.modalita_pagamento || "none"} onValueChange={(v) => setEdit({ ...edit, modalita_pagamento: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="customer-pagamento-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Non indicata</SelectItem>{Object.entries(MODALITA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {agenti.length > 0 && edit.tipo !== "fornitore" && <>
              <Field label="Agente di vendita">
                <Select value={edit.agente_id || "none"} onValueChange={(v) => setEdit({ ...edit, agente_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="customer-agent-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Nessun agente</SelectItem>{agenti.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.provvigione ?? 0}%)</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Provvigione per questo cliente (%)">
                <Input type="number" min="0" max="100" step="0.5" data-testid="customer-commission-input" placeholder="Vuoto = % dell'agente" value={edit.provvigione ?? ""}
                  onChange={(e) => setEdit({ ...edit, provvigione: e.target.value === "" ? null : Number(e.target.value) })} />
              </Field>
            </>}
          </div>
          </TabsContent>
          </Tabs>
        )}
        <DialogFooter><Button data-testid="save-customer-button" className="bg-sky-600 hover:bg-sky-700" onClick={save}>Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ProductDialog = ({ value, onClose, onSaved }) => {
  const [edit, setEdit] = useDraft(value);
  const [mag, setMag] = useState(false);
  const [forn, setForn] = useState([]);
  useEffect(() => {
    api.get("/magazzino/stato").then((r) => setMag(r.data.attivo)).catch(() => {});
    api.get("/customers").then((r) => setForn(r.data.filter((c) => ["fornitore", "entrambi"].includes(c.tipo)))).catch(() => {});
  }, []);
  const set = (k, v) => setEdit({ ...edit, [k]: v });
  const save = async () => {
    try {
      const body = { ...edit, prezzo: Number(edit.prezzo), aliquota: Number(edit.aliquota), scorta_minima: Number(edit.scorta_minima || 0), natura: Number(edit.aliquota) === 0 ? edit.natura || "N2.2" : "" };
      const { data } = edit.id ? await api.put(`/products/${edit.id}`, body) : await api.post("/products", body);
      toast.success("Articolo salvato");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-dialog">
        <DialogHeader><DialogTitle>{edit?.id ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle></DialogHeader>
        {edit && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Codice"><Input data-testid="product-codice-input" value={edit.codice} onChange={(e) => set("codice", e.target.value)} /></Field>
            <Field label="Codice a barre (EAN)"><Input data-testid="product-barcode-input" className="font-mono" placeholder="Spara con il lettore o scrivi" value={edit.barcode || ""} onChange={(e) => set("barcode", e.target.value.trim())} onKeyDown={(e) => e.key === "Enter" && e.preventDefault()} /></Field>
            <Field label="Unità di misura"><Input data-testid="product-unita-input" value={edit.unita} onChange={(e) => set("unita", e.target.value)} /></Field>
            <Field label="Descrizione" className="sm:col-span-2"><Input data-testid="product-descrizione-input" value={edit.descrizione} onChange={(e) => set("descrizione", e.target.value)} /></Field>
            <Field label="Prezzo unitario €"><Input data-testid="product-prezzo-input" type="number" step="0.01" value={edit.prezzo} onChange={(e) => set("prezzo", e.target.value)} /></Field>
            <Field label="Aliquota IVA">
              <Select value={String(edit.aliquota)} onValueChange={(v) => set("aliquota", Number(v))}>
                <SelectTrigger data-testid="product-aliquota-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ALIQUOTE.map((a) => <SelectItem key={a} value={String(a)}>{a}%</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {Number(edit.aliquota) === 0 && (
              <Field label="Natura esenzione" className="sm:col-span-2">
                <Select value={edit.natura || "N2.2"} onValueChange={(v) => set("natura", v)}>
                  <SelectTrigger data-testid="product-natura-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(NATURE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            {mag && <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm sm:col-span-2">
              <span>Gestito a magazzino (bene con giacenza)</span>
              <Switch data-testid="product-magazzino-switch" checked={!!edit.a_magazzino} onCheckedChange={(v) => set("a_magazzino", v)} />
            </label>}
            {mag && edit.a_magazzino && <Field label="Scorta minima (avviso in dashboard sotto questa quantità)" className="sm:col-span-2"><Input data-testid="product-scorta-input" type="number" step="0.001" min="0" value={edit.scorta_minima ?? 0} onChange={(e) => set("scorta_minima", e.target.value)} /></Field>}
            {mag && edit.a_magazzino && (
              <Field label="Fornitore abituale (per il riordino automatico)" className="sm:col-span-2">
                <Select value={edit.fornitore_id || "none"} onValueChange={(v) => set("fornitore_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="product-supplier-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Nessuno</SelectItem>{forn.map((c) => <SelectItem key={c.id} value={c.id}>{c.denominazione || `${c.nome} ${c.cognome}`}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
          </div>
        )}
        <DialogFooter><Button data-testid="save-product-button" onClick={save} disabled={!edit?.descrizione}>Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
